// Visual effects: floating "+$1,200" numbers, coin bursts, the level-up celebration and
// numbers that count up. One <FxLayer/> at the top of the page draws them.
import { useEffect, useRef, useState } from 'react';
import { getSettings, useSettings } from './settings.ts';

// ---------------------------------------------------------------- where the player last clicked
let lastX = window.innerWidth / 2;
let lastY = window.innerHeight / 3;
let lastAt = 0;
export function installPointerTracking() {
  window.addEventListener('pointerdown', (e) => {
    lastX = e.clientX;
    lastY = e.clientY;
    lastAt = Date.now();
  }, { capture: true, passive: true });
}
/** The last click position if it was recent (so effects appear where the player is looking). */
export function lastPointer(): { x: number; y: number } | null {
  return Date.now() - lastAt < 8000 ? { x: lastX, y: lastY } : null;
}

// ---------------------------------------------------------------- tiny event bus
type FxEvent =
  | { kind: 'float'; id: number; text: string; tone: 'cash' | 'loss' | 'xp' | 'gold' | 'bad'; x: number; y: number; delay: number }
  | { kind: 'coins'; id: number; x: number; y: number; n: number }
  | { kind: 'level'; id: number; level: number }
  | { kind: 'stamp'; id: number; text: string; sub: string; tone: 'good' | 'bad' | 'gold'; delay: number };

let seq = 0;
const subs = new Set<(e: FxEvent) => void>();
const emit = (e: FxEvent) => {
  if (getSettings().motion === 'off') return;
  for (const s of subs) s(e);
};

export const fx = {
  /** Float a short text up from a point (defaults to the last click). */
  float(text: string, tone: 'cash' | 'loss' | 'xp' | 'gold' | 'bad', at?: { x: number; y: number } | null, delay = 0) {
    const p = at ?? lastPointer();
    if (!p) return;
    emit({ kind: 'float', id: ++seq, text, tone, x: p.x, y: p.y, delay });
  },
  /** A burst of gold coins. */
  coins(n = 14, at?: { x: number; y: number } | null) {
    const p = at ?? lastPointer() ?? { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    emit({ kind: 'coins', id: ++seq, x: p.x, y: p.y, n: getSettings().motion === 'reduced' ? Math.min(n, 6) : n });
  },
  levelUp(level: number) {
    emit({ kind: 'level', id: ++seq, level });
  },
  /** A big rubber stamp across the middle of the screen ("MISSION COMPLETE"). */
  stamp(text: string, sub = '', tone: 'good' | 'bad' | 'gold' = 'good', delay = 0) {
    emit({ kind: 'stamp', id: ++seq, text, sub, tone, delay });
  },
};

/** The centre of an element on screen, for aiming effects. */
export function centerOf(el: Element | null): { x: number; y: number } | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

// ---------------------------------------------------------------- the layer
export function FxLayer() {
  const [items, setItems] = useState<FxEvent[]>([]);
  useEffect(() => {
    const on = (e: FxEvent) => {
      setItems((xs) => [...xs.slice(-30), e]);
      const life = e.kind === 'level' ? 3200 : e.kind === 'coins' ? 1600 : e.kind === 'stamp' ? 2200 + e.delay : 1700 + (e.kind === 'float' ? e.delay : 0);
      setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== e.id)), life);
    };
    subs.add(on);
    return () => { subs.delete(on); };
  }, []);
  return (
    <div className="fx-layer" aria-hidden="true">
      {items.map((e) => {
        if (e.kind === 'float') {
          return <div key={e.id} className={`fx-float fx-${e.tone}`} style={{ left: e.x, top: e.y, animationDelay: `${e.delay}ms` }}>{e.text}</div>;
        }
        if (e.kind === 'coins') {
          return (
            <div key={e.id} className="fx-coins" style={{ left: e.x, top: e.y }}>
              {Array.from({ length: e.n }).map((_, i) => {
                const ang = (i / e.n) * Math.PI * 2 + Math.random() * 0.5;
                const dist = 40 + Math.random() * 70;
                return <span key={i} className="fx-coin" style={{ '--dx': `${Math.cos(ang) * dist}px`, '--dy': `${Math.sin(ang) * dist - 40}px`, animationDelay: `${Math.random() * 120}ms` } as React.CSSProperties} />;
              })}
            </div>
          );
        }
        if (e.kind === 'stamp') {
          return (
            <div key={e.id} className={`fx-stamp fx-stamp-${e.tone}`} style={{ animationDelay: `${e.delay}ms` }}>
              <div className="fx-stamp-text">{e.text}</div>
              {e.sub && <div className="fx-stamp-sub">{e.sub}</div>}
            </div>
          );
        }
        return <LevelUpBurst key={e.id} level={e.level} onDone={() => setItems((xs) => xs.filter((x) => x.id !== e.id))} />;
      })}
    </div>
  );
}

function LevelUpBurst({ level, onDone }: { level: number; onDone: () => void }) {
  return (
    <div className="fx-levelup" onClick={onDone}>
      <svg className="fx-rays" viewBox="-100 -100 200 200" width="420" height="420">
        {Array.from({ length: 16 }).map((_, i) => (
          <path key={i} d="M0 0 L-9 -100 L9 -100 Z" fill={i % 2 ? '#ffcc33' : '#ff9a1a'} opacity="0.55" transform={`rotate(${i * 22.5})`} />
        ))}
      </svg>
      <div className="fx-levelup-text">
        <div className="fx-levelup-title">LEVEL UP!</div>
        <div className="fx-levelup-num">{level}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- numbers that count up
/** Shows `value`, but animates from the previous value when it changes. */
export function useAnimatedNumber(value: number, ms = 650): number {
  const s = useSettings();
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  const raf = useRef(0);
  const loaded = useRef(value !== 0);
  useEffect(() => {
    cancelAnimationFrame(raf.current);
    const start = from.current;
    const firstLoad = !loaded.current;
    if (value !== 0) loaded.current = true;
    if (firstLoad || !s.countUp || s.motion === 'off' || start === value || Math.abs(value - start) < 2) {
      from.current = value;
      setShown(value);
      return;
    }
    const t0 = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / ms);
      const eased = 1 - Math.pow(1 - k, 3);
      const v = Math.round(start + (value - start) * eased);
      from.current = v;
      setShown(v);
      if (k < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    // Safety net: if the browser pauses animation frames (background tab, screenshot), land on the real value anyway.
    const done = setTimeout(() => { cancelAnimationFrame(raf.current); from.current = value; setShown(value); }, ms + 120);
    return () => { cancelAnimationFrame(raf.current); clearTimeout(done); };
  }, [value]);
  // Before the first real value has arrived, show it straight away (no counting up from $0 after logging in).
  return loaded.current ? shown : value;
}

/** Briefly true after `value` changes (for one-off CSS highlight classes). */
export function usePulse(value: unknown, ms = 900): boolean {
  const [on, setOn] = useState(false);
  const prev = useRef<unknown>(value);
  useEffect(() => {
    const before = prev.current;
    prev.current = value;
    // Not when the value first arrives (e.g. the character sheet loading), only when it changes.
    if (before === undefined || before === null || before === value) return;
    setOn(true);
    const t = setTimeout(() => setOn(false), ms);
    return () => clearTimeout(t);
  }, [value]);
  return on;
}
